// 編輯器狀態：目前的心智圖、選取、復原／重做。所有修改都經過 core 的 applyOps，與 MCP 語意一致。
import { applyOps, parseOutline, indexTopics, walk, type MapOp, type MindMap, type Topic } from '@sind/core';

export type Selection =
  | { kind: 'topic'; id: string }
  | { kind: 'relation'; from: string; to: string }
  | null;

interface Snapshot { map: MindMap; selection: Selection }

export type ChangeReason = 'edit' | 'replace' | 'undo' | 'select';
type Listener = (reason: ChangeReason) => void;

const HISTORY_LIMIT = 100;

export class EditorState {
  map: MindMap;
  selection: Selection = null;
  /** 遞增的版本號；儲存時用來判斷是否有新修改 */
  revision = 0;
  private undoStack: Snapshot[] = [];
  private redoStack: Snapshot[] = [];
  private listeners = new Set<Listener>();

  constructor(map: MindMap) {
    this.map = map;
    this.selection = { kind: 'topic', id: map.root.id };
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(reason: ChangeReason): void {
    for (const listener of this.listeners) listener(reason);
  }

  private snapshot(): Snapshot {
    return { map: this.map, selection: this.selection };
  }

  private pushHistory(): void {
    this.undoStack.push(this.snapshot());
    if (this.undoStack.length > HISTORY_LIMIT) this.undoStack.shift();
    this.redoStack = [];
  }

  // 套用操作；失敗時丟出 OpError，狀態不變。回傳新建立的 id。
  apply(ops: MapOp[], select?: (created: string[]) => Selection): string[] {
    const { map, created } = applyOps(this.map, ops);
    this.pushHistory();
    this.map = map;
    this.revision++;
    if (select) this.selection = select(created);
    this.fixSelection();
    this.emit('edit');
    return created;
  }

  // 整張圖替換（匯入、大綱面板編輯）；可復原。
  replace(map: MindMap, keepHistory = true): void {
    if (keepHistory) this.pushHistory(); else { this.undoStack = []; this.redoStack = []; }
    this.map = map;
    this.revision++;
    this.selection = { kind: 'topic', id: map.root.id };
    this.emit('replace');
  }

  select(selection: Selection): void {
    this.selection = selection;
    this.fixSelection();
    this.emit('select');
  }

  get canUndo(): boolean { return this.undoStack.length > 0; }
  get canRedo(): boolean { return this.redoStack.length > 0; }

  undo(): void {
    const previous = this.undoStack.pop();
    if (!previous) return;
    this.redoStack.push(this.snapshot());
    this.map = previous.map;
    this.selection = previous.selection;
    this.revision++;
    this.fixSelection();
    this.emit('undo');
  }

  redo(): void {
    const next = this.redoStack.pop();
    if (!next) return;
    this.undoStack.push(this.snapshot());
    this.map = next.map;
    this.selection = next.selection;
    this.revision++;
    this.fixSelection();
    this.emit('undo');
  }

  // 選取的主題（若有）。
  get selectedTopic(): Topic | null {
    if (this.selection?.kind !== 'topic') return null;
    return indexTopics(this.map.root).get(this.selection.id) ?? null;
  }

  parentOf(id: string): Topic | null {
    let found: Topic | null = null;
    walk(this.map.root, (topic, _depth, parent) => { if (topic.id === id) found = parent ?? null; });
    return found;
  }

  // 選取的主題被刪除或藏在收合分支中時，改選最近的可見祖先。
  private fixSelection(): void {
    if (!this.selection) return;
    if (this.selection.kind === 'relation') {
      const { from, to } = this.selection;
      if (!this.map.relations.some(relation => relation.from === from && relation.to === to)) this.selection = null;
      return;
    }
    const visible = new Set<string>();
    const ancestors = new Map<string, string>();
    const visit = (topic: Topic, hidden: boolean) => {
      if (!hidden) visible.add(topic.id);
      for (const childTopic of topic.children) { ancestors.set(childTopic.id, topic.id); visit(childTopic, hidden || Boolean(topic.collapsed)); }
    };
    visit(this.map.root, false);
    let id: string | undefined = this.selection.id;
    while (id && !visible.has(id)) id = ancestors.get(id);
    this.selection = { kind: 'topic', id: id ?? this.map.root.id };
  }
}

// 新心智圖：一個中心主題。
export function blankMap(title: string): MindMap {
  return parseOutline(`# ${title}`).map!;
}
